import { useState, useRef, useEffect } from "react";

// --- Date Helpers ---

const formatDate = (date) => {
  if (!date) return "";
  const d = String(date.getDate()).padStart(2, "0");
  const m = date.toLocaleString("en-GB", { month: "short" });
  const y = date.getFullYear();
  return `${d} ${m} ${y}`;
};

const startOfMonth = (date) => {
  return new Date(date.getFullYear(), date.getMonth(), 1);
};

const endOfMonth = (date) => {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
};

const startOfWeek = (date) => {
  const d = new Date(date);
  d.setDate(d.getDate() - d.getDay()); // Sunday = 0
  return d;
};

const endOfWeek = (date) => {
  const d = new Date(date);
  d.setDate(d.getDate() + (6 - d.getDay()));
  return d;
};

const addDays = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

const subtractDays = (date, days) => addDays(date, -days);

const subtractMonths = (date, months) => {
  const d = new Date(date);
  d.setMonth(d.getMonth() - months);
  return d;
};

const isBefore = (a, b) => a.getTime() < b.getTime();
const isAfter = (a, b) => a.getTime() > b.getTime();
const isSameDay = (a, b) =>
  a && b &&
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const getMonthDays = (date) => {
  const start = startOfWeek(startOfMonth(date));
  const end = endOfWeek(endOfMonth(date));

  const days = [];
  let current = new Date(start);

  while (!isAfter(current, end)) {
    days.push(new Date(current));
    current = addDays(current, 1);
  }

  return days;
};

const formatMonthYear = (date) =>
  date.toLocaleString("en-US", { month: "long", year: "numeric" });

// --- Component ---

export default function CustomDateFilter({ onChange, value, isFilterCleared, customStyle={} }) {
  const [open, setOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(value?.[0] ? new Date(value[0]) : new Date());
  const [startDate, setStartDate] = useState(value?.[0] ? new Date(value[1]) : new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [activePreset, setActivePreset] = useState(null);

  const ref = useRef();
  const days = getMonthDays(currentMonth);

  useEffect(() => {
    console.log("value",value)
    if (!open && value && value.length === 2) {
      setStartDate(new Date(value[0]));
      setEndDate(new Date(value[1]));
    }
  }, [value, open]);

  useEffect(() => {
    if (isFilterCleared) {
      const today = new Date();
      setStartDate(today);
      setEndDate(today);
      setActivePreset(null);
    }
  }, [isFilterCleared]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleDateClick = (day) => {
    setActivePreset(null);

    if (!startDate || (startDate && endDate)) {
      setStartDate(day);
      setEndDate(null);
    } else {
      if (isBefore(day, startDate)) {
        setStartDate(day);
      } else {
        setEndDate(day);
        onChange?.([startDate, day]);
      }
    }
  };

  const isInRange = (day) => {
    if (!startDate || !endDate) return false;
    return isAfter(day, startDate) && isBefore(day, endDate);
  };

  const isSelected = (day) =>
    isSameDay(day, startDate) || isSameDay(day, endDate);

  const applyPreset = (type) => {
    setActivePreset(type);
    const today = new Date();
    let start;

    switch (type) {
      case "7":
        start = subtractDays(today, 6);
        break;
      case "15":
        start = subtractDays(today, 14);
        break;
      case "1m":
        start = subtractMonths(today, 1);
        break;
      case "6m":
        start = subtractMonths(today, 6);
        break;
      default:
        return;
    }

    setStartDate(start);
    setEndDate(today);
    onChange?.([start, today]);
  };

  const prevMonth = () => {
    setCurrentMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1));
  };

  const chipStyle = (active) => ({
    padding: "4px 8px",
    borderRadius: 20,
    border: "1px solid #ccc",
    cursor: "pointer",
    background: active ? "#1976d2" : "#f5f5f5",
    color: active ? "#fff" : "#000",
    fontSize: 11,
  });

  return (
    <div ref={ref} style={{ position: "relative", width: 250 }}>
      {/* Input */}
      <div
        onClick={() => setOpen(!open)}
        style={{
          padding: "5px 12px",
          border: "1px solid #ccc",
          borderRadius: 8,
          cursor: "pointer",
          display: "flex",
          justifyContent: "space-between",
          ...customStyle
        }}
      >
        <span>
          {startDate && endDate
            ? `${formatDate(startDate)} - ${formatDate(endDate)}`
            : "Select Date Range"}
        </span>
        <span style={{ marginTop: "2px", fontSize: "10px" }}>▼</span>
      </div>

      {/* Popover */}
      {open && (
        <div
          style={{
            position: "absolute",
            top: 50,
            left: 0,
            background: "#fff",
            border: "1px solid #ddd",
            borderRadius: 12,
            padding: 16,
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            zIndex: 10,
            width: 320,
          }}
        >
          {/* Month Navigation */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: 10,
            }}
          >
            <button onClick={prevMonth}>◀</button>
            <b>{formatMonthYear(currentMonth)}</b>
            <button onClick={nextMonth}>▶</button>
          </div>

          {/* Day Labels */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(7,1fr)",
              fontSize: 12,
              color: "#777",
              marginBottom: 5,
            }}
          >
            {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => (
              <div key={d} style={{ textAlign: "center" }}>
                {d}
              </div>
            ))}
          </div>

          {/* Day Grid */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(7,1fr)",
              gap: 5,
            }}
          >
            {days.map((day, i) => {
              const isCurrentMonth = day.getMonth() === currentMonth.getMonth();

              return (
                <div
                  key={i}
                  onClick={() => handleDateClick(day)}
                  style={{
                    padding: 8,
                    textAlign: "center",
                    cursor: "pointer",
                    borderRadius: 6,
                    background: isSelected(day)
                      ? "#1976d2"
                      : isInRange(day)
                        ? "#bbdefb"
                        : "transparent",
                    color: isSelected(day)
                      ? "#fff"
                      : isCurrentMonth
                        ? "#000"
                        : "#bbb",
                  }}
                >
                  {day.getDate()}
                </div>
              );
            })}
          </div>

          {/* Preset Chips */}
          <div style={{ display: "flex", gap: 8, marginTop: 15, flexWrap: "wrap" }}>
            {[
              { label: "7 Days", value: "7" },
              { label: "15 Days", value: "15" },
              { label: "1 Month", value: "1m" },
              { label: "6 Months", value: "6m" },
            ].map(({ label, value }) => (
              <div
                key={value}
                style={chipStyle(activePreset === value)}
                onClick={() => applyPreset(value)}
              >
                {label}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}